// src/components/dentally/DentallyAppointmentSyncNotice.jsx
//
// Tells the user, before they commit, what saving will do to Dentally.
//
// Renders nothing at all unless the ROUTE practice has an active Dentally
// integration the user may view (useDentallyIntegrationScope — never the
// app-global dentallyEnabledGlobal flag on its own, which leaked Dentally UI
// into non-Dentally practices in September) AND this appointment actually
// carries a Dentally link. A CRM-only appointment in a Dentally practice shows
// nothing, exactly as it does today.
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import Iconify from '../Iconify';
import DentallyAppointmentBadge from './DentallyAppointmentBadge';
import useDentallyIntegrationScope from '../../hooks/useDentallyIntegrationScope';
import { TONE_COLOURS } from '../../utils/dentallyPatientLinkStatus';
import {
  APPOINTMENT_SYNC_INTENT,
  APPOINTMENT_SYNC_NOTICE,
  getAppointmentSyncIntent,
} from '../../utils/dentallyAppointmentSync';

export default function DentallyAppointmentSyncNotice({ dentally = null, practiceId = undefined, sx = undefined }) {
  const scope = useDentallyIntegrationScope(practiceId);
  const intent = getAppointmentSyncIntent(dentally);

  if (!scope.visible) return null;
  if (intent === APPOINTMENT_SYNC_INTENT.none) return null;

  const copy = APPOINTMENT_SYNC_NOTICE[
    { [APPOINTMENT_SYNC_INTENT.willSync]: 'willSync',
      [APPOINTMENT_SYNC_INTENT.needsReview]: 'needsReview' }[intent] || 'unconfirmed'
  ];
  const colours = TONE_COLOURS[copy.tone];

  return (
    <Box
      data-testid="dentally-appointment-sync-notice"
      data-intent={intent}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.5,
        borderRadius: 1,
        bgcolor: colours.bg,
        ...sx,
      }}
    >
      <Iconify
        icon={copy.icon}
        width={20}
        height={20}
        sx={{ color: colours.fg, flexShrink: 0, mt: '2px' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: colours.fg }}>
          {copy.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
          {copy.body}
        </Typography>
      </Box>
      <DentallyAppointmentBadge dentally={dentally} practiceId={practiceId} sx={{ flexShrink: 0 }} />
    </Box>
  );
}

DentallyAppointmentSyncNotice.propTypes = {
  dentally: PropTypes.shape({
    appointment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creation_state: PropTypes.string,
    cancellation_state: PropTypes.string,
    reconciliation_required: PropTypes.bool,
  }),
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
};
